import { StrictMode } from 'react'
import type { VFC } from 'react'
import { isNonNegativeNumber } from '@opallabs/lambda'
import * as styles from './styles'
import { textFallback } from './textFallback'
import type { BudgetMeterProps } from './types'

/**
 * Visual representation of how much of an available budget has been allocated.
 * This is a simple wrapper around `<meter>` that does basic input validation
 * and provides accessible text.
 *
 * @note By default currency values are in US Dollars (`USD`) because it's what
 *   most of our users will expect.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/meter
 */
export const BudgetMeter: VFC<BudgetMeterProps> = ({
  ariaLabelledBy,
  currency = 'USD',
  max,
  styleOverrides,
  theme = 'light',
  value,
}) => {
  if (!isNonNegativeNumber(max)) {
    console.warn(
      `Passed an invalid 'max' (${max}) to <BudgetMeter>; defaulting to 0`
    )
    max = 0
  }

  let sanitizedValue = value
  if (!isNonNegativeNumber(value)) {
    console.warn(
      `Passed an invalid 'value' (${value || 'undefined'}) to <BudgetMeter>`
    )
    sanitizedValue = undefined
  }

  return (
    <StrictMode>
      <meter
        aria-labelledby={ariaLabelledBy}
        css={[
          styles.container({
            overSpent: Boolean(sanitizedValue && sanitizedValue > max),
            theme,
          }),
          styleOverrides,
        ]}
        max={max}
        value={sanitizedValue}
      >
        {textFallback({
          currency,
          max,
          value: sanitizedValue,
        })}
      </meter>
    </StrictMode>
  )
}
BudgetMeter.displayName = 'Photon: BudgetMeter'
